/** Stack §8 error envelope, surfaced as a typed client-side error. */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Shared by api() and apiWithMeta() below — the actual fetch/error handling
 * is identical, they only differ in whether the caller wants `meta` too. */
async function apiEnvelope(path: string, init?: RequestInit): Promise<unknown> {
  // FormData (multipart uploads) must NOT get an explicit Content-Type — the
  // browser sets it itself, boundary included. Setting it here breaks the
  // multipart parse on the server.
  const isFormData = init?.body instanceof FormData;
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body && !isFormData ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });

  if (!res.ok) {
    let code = "http_error";
    let message = res.statusText;
    let details: unknown;
    try {
      const body = await res.json();
      code = body?.error?.code ?? code;
      message = body?.error?.message ?? message;
      details = body?.error?.details;
    } catch {
      // Non-JSON error page (e.g. an nginx 502) — keep the status text.
    }
    throw new ApiClientError(res.status, code, message, details);
  }

  if (res.status === 204) return {};
  return res.json();
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const body = (await apiEnvelope(path, init)) as { data?: T };
  // Every endpoint wraps its payload in `data`.
  return (body?.data ?? body) as T;
}

/** Same as api(), but keeps `meta` too — for endpoints that paginate
 * (`{ data, meta: { limit, offset, total } }`), where the caller needs the
 * total to build real pagination instead of guessing from page size. */
export async function apiWithMeta<T, M = unknown>(
  path: string,
  init?: RequestInit,
): Promise<{ data: T; meta: M }> {
  const body = (await apiEnvelope(path, init)) as { data: T; meta: M };
  return body;
}
